import React, { useState, useRef } from 'react';
import { Sparkles, Info, ShieldCheck, ChevronDown, Mail } from 'lucide-react';
type RuleAction = 'auto-approve' | 'auto-reject' | 'human-review';
interface Rule {
  id: string;
  label: string;
  description: string;
  action: RuleAction;
}
const actionOptions: {
  value: RuleAction;
  label: string;
  color: string;
}[] = [{
  value: 'auto-approve',
  label: 'Auto Approve Below Threshold',
  color: 'text-emerald-700 bg-emerald-50 border-emerald-200'
}, {
  value: 'auto-reject',
  label: 'Auto Reject / Write-down',
  color: 'text-rose-700 bg-rose-50 border-rose-200'
}, {
  value: 'human-review',
  label: 'Require Human Review',
  color: 'text-amber-700 bg-amber-50 border-amber-200'
}];
function ActionDropdown({
  value,
  onChange
}: {
  value: RuleAction;
  onChange: (v: RuleAction) => void;
}) {
  const [open, setOpen] = useState(false);
  const [dropPos, setDropPos] = useState({
    top: 0,
    left: 0,
    width: 0
  });
  const btnRef = useRef<HTMLButtonElement>(null);
  const selected = actionOptions.find(o => o.value === value)!;
  const handleOpen = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setDropPos({
        top: rect.bottom + window.scrollY + 4,
        left: rect.right + window.scrollX,
        width: Math.max(rect.width, 220)
      });
    }
    setOpen(o => !o);
  };
  return <div className="relative" data-id="element-874">
      <button ref={btnRef} onClick={handleOpen} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-colors ${selected.color} min-w-[170px] justify-between`} data-id="element-875">
        <span data-id="element-876">{selected.label}</span>
        <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-877" />
      </button>
      {open && <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} data-id="element-878" />
          <div className="fixed z-50 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden" style={{
        top: dropPos.top,
        left: dropPos.left - dropPos.width,
        minWidth: dropPos.width
      }} data-id="element-879">
            {actionOptions.map(opt => <button key={opt.value} onClick={() => {
          onChange(opt.value);
          setOpen(false);
        }} className={`w-full text-left px-4 py-2.5 text-xs font-semibold transition-colors hover:bg-slate-50 ${opt.value === value ? opt.color : 'text-slate-700'}`} data-id="element-880">
                {opt.label}
              </button>)}
          </div>
        </>}
    </div>;
}
function RuleRow({
  rule,
  onChange
}: {
  rule: Rule;
  onChange: (id: string, action: RuleAction) => void;
}) {
  return <div className="flex items-center justify-between py-4 border-b border-slate-100 last:border-0" data-id="element-881">
      <div className="flex-1 pr-8" data-id="element-882">
        <p className="text-sm font-semibold text-slate-800" data-id="element-883">{rule.label}</p>
        <p className="text-xs text-slate-500 mt-0.5" data-id="element-884">{rule.description}</p>
      </div>
      <ActionDropdown value={rule.action} onChange={v => onChange(rule.id, v)} data-id="element-885" />
    </div>;
}
export function Rules() {
  const [approvalRules, setApprovalRules] = useState<Rule[]>([{
    id: 'short-quantity-bol',
    label: 'Short Quantity — BOL Mismatch',
    description: "Auto-approve disputes where the BOL quantity exceeds the retailer's claimed received quantity.",
    action: 'auto-approve'
  }, {
    id: 'high-quantity-bol',
    label: 'High Quantity / BOL Mismatch',
    description: 'Handle disputes where the retailer claims more units than the BOL confirms were shipped.',
    action: 'auto-approve'
  }, {
    id: 'price-discrepancy',
    label: 'Price Discrepancy — Invoice Match',
    description: 'Auto-approve when our invoice price matches the PO price and the retailer deducted a different amount.',
    action: 'human-review'
  }, {
    id: 'damaged-goods',
    label: 'Damaged Goods — FOB Origin',
    description: 'Auto-approve damaged goods claims where the shipment terms are FOB Origin and damage occurred post-pickup.',
    action: 'human-review'
  }, {
    id: 'allowance-duplicate',
    label: 'Allowance — Allowances',
    description: 'Agreed upon trade promotion allowances.',
    action: 'auto-approve'
  }]);
  const [thresholdRules, setThresholdRules] = useState<Rule[]>([{
    id: 'auto-approve-under-500',
    label: 'Disputes under $500',
    description: 'Skip human review for disputes where the deduction amount is below $500 and AI confidence is high.',
    action: 'human-review'
  }, {
    id: 'auto-approve-under-1000',
    label: 'Disputes under $1,000',
    description: 'Skip human review for disputes where the deduction amount is below $1,000 and AI confidence is high.',
    action: 'auto-approve'
  }, {
    id: 'flag-over-5000',
    label: 'Disputes over $5,000',
    description: 'Regardless of AI confidence, route any dispute over $5,000 to a human analyst before sending.',
    action: 'human-review'
  }]);
  const [thresholdAmount, setThresholdAmount] = useState('5000');
  const [reminderEnabled, setReminderEnabled] = useState(true);
  const [reminderDays, setReminderDays] = useState('90');
  const updateApproval = (id: string, action: RuleAction) => setApprovalRules(prev => prev.map(r => r.id === id ? {
    ...r,
    action
  } : r));
  const updateThreshold = (id: string, action: RuleAction) => setThresholdRules(prev => prev.map(r => r.id === id ? {
    ...r,
    action
  } : r));
  return <div className="space-y-8" data-id="element-886">
      {/* Header */}
      <div data-id="element-887">
        <h2 className="text-2xl font-bold text-slate-900 tracking-tight" data-id="element-888">
          AI Automation Rules
        </h2>
        <p className="text-slate-500 mt-1" data-id="element-889">
          Define which deductions the AI can automatically approve and process.
        </p>
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-xl px-5 py-4" data-id="element-890">
        <ShieldCheck className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" data-id="element-891" />
        <p className="text-sm text-blue-800" data-id="element-892">
          <span className="font-semibold" data-id="element-893">Note:</span> In all cases, if the AI
          isn't confident, it will loop in a human to review before any action
          is taken.
        </p>
      </div>

      {/* Section 1: Approval by Claim Type */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-id="element-894">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2" data-id="element-895">
          <Sparkles className="w-4 h-4 text-violet-500" data-id="element-896" />
          <h3 className="font-semibold text-slate-900 text-sm" data-id="element-897">
            Approval by Claim Type
          </h3>
        </div>
        <div className="px-6" data-id="element-898">
          {approvalRules.map(rule => <RuleRow key={rule.id} rule={rule} onChange={updateApproval} data-id="element-899" />)}
        </div>
      </div>

      {/* Section 2: Approval Threshold */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-id="element-900">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2" data-id="element-901">
          <Info className="w-4 h-4 text-amber-500" data-id="element-902" />
          <h3 className="font-semibold text-slate-900 text-sm" data-id="element-903">
            Approval Threshold
          </h3>
        </div>
        <div className="px-6 py-4" data-id="element-904">
          <div className="flex items-center justify-between" data-id="element-905">
            <div className="flex-1 pr-8" data-id="element-906">
              <p className="text-sm font-semibold text-slate-800" data-id="element-907">
                Human review over specific amount
              </p>
              <p className="text-xs text-slate-500 mt-0.5" data-id="element-908">
                Any dispute above this amount will be routed to a human analyst
                for review, regardless of AI confidence.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0" data-id="element-909">
              <span className="text-sm font-medium text-slate-500" data-id="element-910">$</span>
              <input type="number" value={thresholdAmount} onChange={e => setThresholdAmount(e.target.value)} className="w-28 text-sm border border-slate-200 rounded-lg px-3 py-1.5 text-slate-800 font-semibold focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-slate-50 text-right" min="0" step="500" data-id="element-911" />
            </div>
          </div>
        </div>
      </div>

      {/* Section 3: Send Email Reminders */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-id="element-912">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2" data-id="element-913">
          <Mail className="w-4 h-4 text-blue-500" data-id="element-914" />
          <h3 className="font-semibold text-slate-900 text-sm" data-id="element-915">
            Send Email Reminders
          </h3>
        </div>
        <div className="px-6 py-4" data-id="element-916">
          <div className="flex items-center justify-between" data-id="element-917">
            <p className="text-sm font-semibold text-slate-800" data-id="element-918">
              Send reminders to unanswered claims emails
            </p>
            <div className="flex items-center gap-4 flex-shrink-0" data-id="element-919">
              {/* Yes/No toggle */}
              <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5" data-id="element-920">
                <button onClick={() => setReminderEnabled(true)} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${reminderEnabled ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-921">
                  Yes
                </button>
                <button onClick={() => setReminderEnabled(false)} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${!reminderEnabled ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-922">
                  No
                </button>
              </div>
              {/* Days input — only shown when enabled */}
              {reminderEnabled && <div className="flex items-center gap-2" data-id="element-923">
                  <input type="number" value={reminderDays} onChange={e => setReminderDays(e.target.value)} className="w-16 text-sm border border-slate-200 rounded-lg px-3 py-1.5 text-slate-800 font-semibold focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-slate-50 text-right" min="1" data-id="element-924" />
                  <span className="text-sm text-slate-500 font-medium" data-id="element-925">
                    days
                  </span>
                </div>}
            </div>
          </div>
        </div>
      </div>
    </div>;
}